import { z } from 'zod';
import { PERMISSIONS, TENANT_ROLES } from './permissions';

// Fallback for messages not customized below; user-facing copy is Spanish.
z.config(z.locales.es());

export const AGENT_STATUSES = ['draft', 'active', 'paused'] as const;
export const RUN_STATUSES = ['queued', 'running', 'completed', 'failed', 'timed_out', 'cancelled'] as const;
export const TERMINAL_RUN_STATUSES = ['completed', 'failed', 'timed_out', 'cancelled'] as const;
export const RUN_EVENT_TYPES = [
  'status',
  'message',
  'message_delta',
  'tool_call',
  'tool_result',
  'policy',
  'retrieval',
  'error',
] as const;

export const agentStatusSchema = z.enum(AGENT_STATUSES);
export const runStatusSchema = z.enum(RUN_STATUSES);
export const tenantRoleSchema = z.enum(TENANT_ROLES);

const trimmed = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min, min === 1 ? 'Este campo es obligatorio.' : `Escribe al menos ${min} caracteres.`)
    .max(max, `Máximo ${max} caracteres.`);

const email = () => z.email('Escribe un correo válido.').max(254);

export const agentConfigSchema = z.object({
  model: trimmed(1, 100),
  temperature: z
    .number('Escribe un número.')
    .min(0, 'Debe estar entre 0 y 2.')
    .max(2, 'Debe estar entre 0 y 2.')
    .default(0.3),
  maxToolCalls: z
    .number('Escribe un número.')
    .int('Debe ser un número entero.')
    .min(0, 'Debe estar entre 0 y 50.')
    .max(50, 'Debe estar entre 0 y 50.')
    .default(10),
  /** ModelDataPolicy: cards and credentials are always masked; personal data only when enabled. */
  dataPolicy: z
    .object({ redactPersonalData: z.boolean().default(false) })
    .default({ redactPersonalData: false }),
});

export const createAgentSchema = z.object({
  name: trimmed(2, 80),
  description: trimmed(0, 500).default(''),
  instructions: trimmed(10, 20_000),
  config: agentConfigSchema,
});

export const updateAgentSchema = z
  .object({
    name: trimmed(2, 80),
    description: trimmed(0, 500),
    instructions: trimmed(10, 20_000),
    config: agentConfigSchema,
    status: z.enum(['active', 'paused']),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'No hay cambios para guardar.' });

export const setAgentToolsSchema = z.object({
  toolIds: z.array(z.string().regex(/^[a-z0-9_.-]{2,80}$/)).max(50),
});

export const setAgentKnowledgeSchema = z.object({
  sourceIds: z.array(z.uuid()).max(50),
});

export const createRunSchema = z.object({
  input: trimmed(1, 10_000),
});

export const createTenantSchema = z.object({
  name: trimmed(2, 120),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/, 'Usa de 3 a 40 minúsculas, números o guiones.'),
  adminEmail: email(),
});

export const addMemberSchema = z.object({
  email: email(),
  role: tenantRoleSchema,
});

export const listQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const runEventsQuerySchema = z.object({
  after: z.coerce.number().int().min(0).default(0),
});

export type AgentStatus = z.infer<typeof agentStatusSchema>;
export type RunStatus = z.infer<typeof runStatusSchema>;
export type RunEventType = (typeof RUN_EVENT_TYPES)[number];
export type AgentConfig = z.infer<typeof agentConfigSchema>;
export type CreateAgentInput = z.infer<typeof createAgentSchema>;
export type UpdateAgentInput = z.infer<typeof updateAgentSchema>;
export type CreateRunInput = z.infer<typeof createRunSchema>;
export type CreateTenantInput = z.infer<typeof createTenantSchema>;
export type AddMemberInput = z.infer<typeof addMemberSchema>;

export const permissionSchema = z.enum(PERMISSIONS);

// Reasoning-style models only accept the default temperature; keep in sync with services/agent-runtime models.py.
const NO_TEMPERATURE_PREFIXES = ['gpt-5', 'gpt-6', 'o1', 'o3', 'o4'];
export const modelSupportsTemperature = (model: string) =>
  !NO_TEMPERATURE_PREFIXES.some((p) => model.startsWith(p));
